import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function TermsPage() {
  const lastUpdated = '2026-06-15';

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="container-page">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-5 tracking-wide">
                LEGAL
              </span>
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                Terms of Service
              </h1>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                The rules and agreements that govern your use of the Freshhlock platform. Please read them carefully.
              </p>
              <p className="text-xs text-foreground-500 mt-4">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="bg-background-50 py-12 md:py-16">
          <div className="container-page">
            <div className="max-w-3xl mx-auto space-y-10 text-foreground-700 leading-relaxed text-sm md:text-base">

              {/* Section 1 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">1. Acceptance of Terms</h2>
                <p>
                  By accessing or using the Freshhlock platform (&ldquo;Platform&rdquo;), including our website, mobile applications, and APIs, you agree to be bound by these Terms of Service (&ldquo;Terms&rdquo;). If you are using the Platform on behalf of a business or other legal entity, you represent that you have the authority to bind that entity to these Terms.
                </p>
                <p className="mt-3">
                  If you do not agree to these Terms, you may not access or use the Platform.
                </p>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">2. Definitions</h2>
                <div className="space-y-2.5">
                  <p><strong className="text-foreground-900">&ldquo;Platform&rdquo;</strong> — The Freshhlock digital orchestration platform, including all associated web interfaces, mobile applications, APIs, and services.</p>
                  <p><strong className="text-foreground-900">&ldquo;User,&rdquo; &ldquo;You&rdquo;</strong> — Any individual or entity that accesses or uses the Platform, including warehouse operators, transport providers, processors, wholesale suppliers, retail buyers, and visitors.</p>
                  <p><strong className="text-foreground-900">&ldquo;Services&rdquo;</strong> — Cold-chain matching, booking, tracking, payment facilitation, quality verification, and related logistics coordination services provided through the Platform.</p>
                  <p><strong className="text-foreground-900">&ldquo;Content&rdquo;</strong> — All text, images, data, listings, ratings, reviews, messages, and other materials uploaded or transmitted through the Platform.</p>
                </div>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">3. Account Registration &amp; Eligibility</h2>
                <p className="mb-3">
                  To access certain features, you must create an account and provide accurate, complete, and current information. You must:
                </p>
                <ul className="space-y-2 list-disc pl-5">
                  <li>Be at least 18 years of age and capable of forming a legally binding contract.</li>
                  <li>If registering as a business, possess valid GST registration, FSSAI licence (where applicable), PAN, and any other regulatory certifications required for your category of operation.</li>
                  <li>Maintain the confidentiality of your account credentials and notify us immediately of any unauthorised use.</li>
                  <li>Be responsible for all activity occurring under your account.</li>
                </ul>
                <p className="mt-3">
                  We reserve the right to suspend or terminate accounts that provide false information, violate these Terms, or engage in fraudulent or harmful conduct.
                </p>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">4. Platform Role &amp; Liability</h2>
                <p>
                  Freshhlock operates as a technology platform that facilitates connections and transactions between independent third parties — warehouse operators, transport providers, processors, suppliers, and buyers. We are <strong className="text-foreground-900">not</strong> a party to any underlying contract for warehousing, transportation, processing, or sale of goods between Platform users.
                </p>
                <p className="mt-3">
                  We do not own, operate, or control any warehouses, vehicles, processing facilities, or inventory listed on the Platform. We do not guarantee the quality, safety, legality, or delivery of any goods or services procured through the Platform. Users transact at their own risk and are encouraged to conduct independent due diligence.
                </p>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">5. User Obligations &amp; Prohibited Conduct</h2>
                <p className="mb-3">You agree not to:</p>
                <ul className="space-y-2 list-disc pl-5">
                  <li>Use the Platform for any unlawful purpose or in violation of any applicable Indian law or regulation.</li>
                  <li>List, store, transport, or trade goods that are illegal, hazardous (beyond standard frozen food), counterfeit, or infringing on intellectual property rights.</li>
                  <li>Manipulate pricing, engage in collusive bidding, or artificially inflate ratings and reviews.</li>
                  <li>Interfere with the Platform&apos;s operation through viruses, bots, scraping, denial-of-service attacks, or other malicious code.</li>
                  <li>Misrepresent your identity, business credentials, certifications, or the nature and quality of goods or services offered.</li>
                  <li>Circumvent the Platform to transact directly with another user discovered through Freshhlock with the intent to avoid platform fees (anti-circumvention).</li>
                </ul>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">6. Fees &amp; Payments</h2>
                <p>
                  Freshhlock charges transaction-based service fees and/or subscription fees as disclosed at the time of booking or as set forth in a separate commercial agreement. All fees are non-refundable except where expressly stated or required by law.
                </p>
                <p className="mt-3">
                  Payments between users (e.g., buyer to supplier, shipper to transporter) are facilitated through third-party payment processors integrated with the Platform. Freshhlock is not responsible for delays, failures, or disputes arising from payment processing. Users are responsible for all applicable taxes, including GST.
                </p>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">7. Intellectual Property</h2>
                <p>
                  The Platform, including its branding, logo, domain name, software code, design, user interface, algorithms, and proprietary data models, is owned by Freshhlock Technologies Pvt. Ltd. and protected by Indian copyright, trademark, and intellectual property laws. You may not copy, modify, distribute, or create derivative works of the Platform without our express written permission.
                </p>
                <p className="mt-3">
                  By uploading Content to the Platform, you grant Freshhlock a non-exclusive, royalty-free, worldwide licence to display, reproduce, and distribute that Content solely for the purpose of operating and promoting the Platform. You retain all ownership rights to your Content.
                </p>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">8. Disclaimers &amp; Limitation of Liability</h2>
                <p>
                  THE PLATFORM IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, FRESHLOCK DISCLAIMS ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className="mt-3">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, FRESHLOCK SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, GOODWILL, OR BUSINESS OPPORTUNITY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE PLATFORM, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY.
                </p>
                <p className="mt-3">
                  Our aggregate liability for any claim arising out of these Terms or the Platform shall not exceed the total fees paid by you to Freshhlock in the twelve (12) months preceding the event giving rise to the claim, or INR 10,000, whichever is greater.
                </p>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">9. Indemnification</h2>
                <p>
                  You agree to indemnify, defend, and hold harmless Freshhlock, its directors, officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or related to: (a) your violation of these Terms; (b) your use of the Platform; (c) any Content you upload; or (d) your products, services, or operations conducted through the Platform.
                </p>
              </div>

              {/* Section 10 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">10. Termination</h2>
                <p>
                  You may terminate your account at any time by contacting us. We may suspend or terminate your access to the Platform at our sole discretion, with or without notice, for any violation of these Terms or for any other reason, including extended inactivity.
                </p>
                <p className="mt-3">
                  Upon termination, your right to use the Platform ceases immediately. Provisions regarding intellectual property, disclaimers, limitation of liability, indemnification, and governing law survive termination.
                </p>
              </div>

              {/* Section 11 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">11. Governing Law &amp; Dispute Resolution</h2>
                <p>
                  These Terms are governed by the laws of the Republic of India. Any dispute arising out of or relating to these Terms shall first be attempted to be resolved through good-faith negotiation. If unresolved within 30 days, the dispute shall be referred to binding arbitration in New Delhi under the Arbitration and Conciliation Act, 1996, with proceedings conducted in English by a sole arbitrator mutually appointed by the parties.
                </p>
                <p className="mt-3">
                  Subject to the arbitration provision above, the courts in New Delhi shall have exclusive jurisdiction over any matters relating to these Terms.
                </p>
              </div>

              {/* Section 12 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">12. Changes to Terms</h2>
                <p>
                  We may update these Terms from time to time. Material changes will be communicated via email and/or a prominent notice on the Platform at least 15 days before the changes take effect. Continued use of the Platform after the effective date constitutes acceptance of the revised Terms.
                </p>
              </div>

              {/* Section 13 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">13. Contact</h2>
                <p>For questions regarding these Terms, contact us at:</p>
                <div className="mt-3 p-5 rounded-lg bg-card-50 border border-card-200 space-y-1.5 text-sm shadow-sm">
                  <p><strong className="text-foreground-900">Email:</strong> legal@freshhlock.in</p>
                  <p><strong className="text-foreground-900">Address:</strong> Freshhlock Technologies Pvt. Ltd., 4th Floor, DLF Cyber City, Gurugram, Haryana — 122002, India</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}